/**
 * "Also editing" (FD §3.4): `usePresence(planId)` sends a heartbeat every 20 s while a plan is open
 * and returns the names of the others who have it open; `PresenceBanner` shows the warning.
 */
import { useEffect, useState } from 'react';
import * as api from '../../api';
import type { PlanId } from '../../../shared/types';

/** Heartbeat interval (the server forgets a name 60 s after its last heartbeat). */
export const PRESENCE_EVERY_MS = 20_000;

/** React hook: the other people who have this plan open right now (sorted names). */
export function usePresence(planId: PlanId | null): string[] {
  const [others, setOthers] = useState<string[]>([]);
  useEffect(() => {
    if (!planId) return;
    let stopped = false;
    const beat = () => {
      api.presence(planId).then(
        (r) => !stopped && setOthers((prev) => (same(prev, r.others) ? prev : r.others)),
        () => undefined, // offline: keep the last answer; the save indicator already says so
      );
    };
    beat();
    const timer = setInterval(beat, PRESENCE_EVERY_MS);
    // Beat again on coming back to the tab, so the banner is fresh.
    const onVisible = () => document.visibilityState === 'visible' && beat();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      setOthers([]);
    };
  }, [planId]);
  return others;
}

function same(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** "Sam", "Sam and Alex", "Sam, Alex and Kim". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

interface BannerProps {
  others: string[];
  onDismiss?: () => void;
}

/** The warning shown when someone else has the same plan open. Renders nothing when nobody does. */
export function PresenceBanner({ others, onDismiss }: BannerProps) {
  if (others.length === 0) return null;
  return (
    <div className="banner info" role="status">
      <span aria-hidden="true">👋</span>
      <span>
        <strong>{joinNames(others)}</strong> {others.length === 1 ? 'is' : 'are'} also editing this plan. Changes may
        overwrite each other, but nothing is lost: earlier states are kept in History.
      </span>
      {onDismiss && (
        <button type="button" className="icon-btn" aria-label="Hide this message" onClick={onDismiss}>
          ✕
        </button>
      )}
    </div>
  );
}
